"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HashnodeContentProvider = void 0;
exports.createContentProvider = createContentProvider;
const config_1 = require("../constants/config");
const local_content_1 = require("./local-content");
class HashnodeContentProvider {
    constructor(hashnodeService) {
        this.cacheKey = config_1.STORAGE_KEYS.POSTS_CACHE;
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
        this.hashnodeService = hashnodeService;
    }
    getCachedData(key) {
        try {
            const cached = localStorage.getItem(`${this.cacheKey}-${key}`);
            if (!cached)
                return null;
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp > this.cacheTimeout) {
                localStorage.removeItem(`${this.cacheKey}-${key}`);
                return null;
            }
            return data;
        }
        catch {
            return null;
        }
    }
    setCachedData(key, data) {
        try {
            localStorage.setItem(`${this.cacheKey}-${key}`, JSON.stringify({
                data,
                timestamp: Date.now()
            }));
        }
        catch {
            // Ignore cache errors
        }
    }
    async getPosts(limit = config_1.CONFIG.POSTS_PER_PAGE, offset = 0) {
        const cacheKey = `posts-${limit}-${offset}`;
        const cached = this.getCachedData(cacheKey);
        if (cached) {
            return cached;
        }
        try {
            const result = await this.hashnodeService.getPosts(limit);
            const transformedPosts = result.posts.map(post => this.hashnodeService.transformToBlogPost(post));
            this.setCachedData(cacheKey, transformedPosts);
            return transformedPosts;
        }
        catch (error) {
            console.error('Failed to fetch posts from Hashnode:', error);
            return [];
        }
    }
    async getPostBySlug(slug) {
        const cacheKey = `post-${slug}`;
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        try {
            const hashnodePost = await this.hashnodeService.getPostBySlug(slug);
            if (!hashnodePost)
                return null;
            const transformedPost = this.hashnodeService.transformToBlogPost(hashnodePost);
            this.setCachedData(cacheKey, transformedPost);
            return transformedPost;
        }
        catch (error) {
            console.error(`Failed to fetch post ${slug} from Hashnode:`, error);
            return null;
        }
    }
    async getMetadata() {
        const cacheKey = 'metadata';
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        try {
            const publication = await this.hashnodeService.getPublication();
            const metadata = this.hashnodeService.transformToBlogMetadata(publication);
            this.setCachedData(cacheKey, metadata);
            return metadata;
        }
        catch (error) {
            console.error('Failed to fetch metadata from Hashnode:', error);
            // Return default metadata
            return {
                siteTitle: config_1.CONFIG.SITE_TITLE,
                siteDescription: config_1.CONFIG.SITE_DESCRIPTION,
                siteUrl: config_1.CONFIG.SITE_URL,
                authorName: config_1.CONFIG.AUTHOR_NAME,
                authorEmail: config_1.CONFIG.AUTHOR_EMAIL,
                socialLinks: {},
                theme: {
                    primaryColor: '#2563eb',
                    accentColor: '#3b82f6',
                    backgroundColor: '#ffffff',
                    textColor: '#111827',
                },
                navigation: [
                    { label: 'Home', href: '/', external: false, order: 1 },
                    { label: 'Posts', href: '/posts', external: false, order: 2 },
                    { label: 'About', href: '/about', external: false, order: 3 },
                ],
                featuredPostsCount: config_1.CONFIG.FEATURED_POSTS_COUNT,
                postsPerPage: config_1.CONFIG.POSTS_PER_PAGE,
            };
        }
    }
    async searchPosts(query) {
        // For Hashnode, we'll fetch all posts and search locally
        const posts = await this.getPosts(50); // Hashnode API limit is 50
        const lowercaseQuery = query.toLowerCase();
        return posts.filter(post => post.title.toLowerCase().includes(lowercaseQuery) ||
            post.content.toLowerCase().includes(lowercaseQuery) ||
            post.excerpt.toLowerCase().includes(lowercaseQuery) ||
            post.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery)));
    }
    async getFeaturedPosts() {
        const posts = await this.getPosts();
        return posts.filter(post => post.featured).slice(0, config_1.CONFIG.FEATURED_POSTS_COUNT);
    }
    async getPostsByCategory(category) {
        const posts = await this.getPosts(50); // Hashnode API limit is 50
        return posts.filter(post => post.category === category);
    }
    async getPostsByTag(tag) {
        const posts = await this.getPosts(50); // Hashnode API limit is 50
        return posts.filter(post => post.tags.includes(tag));
    }
    async getSeriesPosts(seriesSlug) {
        const cacheKey = `series-posts-${seriesSlug}`;
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        try {
            const seriesPosts = await this.hashnodeService.getSeriesPosts(seriesSlug);
            const transformedPosts = seriesPosts.map(post => this.hashnodeService.transformToBlogPost(post));
            this.setCachedData(cacheKey, transformedPosts);
            return transformedPosts;
        }
        catch (error) {
            console.error('Failed to fetch series posts:', error);
            return [];
        }
    }
    async getTags() {
        const cacheKey = 'tags';
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        const posts = await this.getPosts(50); // Hashnode API limit is 50
        const tagSet = new Set();
        posts.forEach(post => post.tags.forEach(tag => tagSet.add(tag)));
        const tags = Array.from(tagSet).sort();
        this.setCachedData(cacheKey, tags);
        return tags;
    }
    async getCategories() {
        const cacheKey = 'categories';
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        const posts = await this.getPosts(50); // Hashnode API limit is 50
        const categorySet = new Set();
        posts.forEach(post => categorySet.add(post.category));
        const categories = Array.from(categorySet).sort();
        this.setCachedData(cacheKey, categories);
        return categories;
    }
    async getSeries() {
        const cacheKey = 'series';
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        try {
            const seriesData = await this.hashnodeService.getSeries();
            const transformedSeries = seriesData.map(series => ({
                id: series.id,
                name: series.name,
                slug: series.slug,
                posts: series.posts.map(post => this.hashnodeService.transformToBlogPost(post))
            }));
            this.setCachedData(cacheKey, transformedSeries);
            return transformedSeries;
        }
        catch (error) {
            console.error('Failed to fetch series:', error);
            return [];
        }
    }
    async getDraft(draftId) {
        const cacheKey = `draft-${draftId}`;
        const cached = this.getCachedData(cacheKey);
        if (cached)
            return cached;
        try {
            const draft = await this.hashnodeService.getDraft(draftId);
            if (draft) {
                this.setCachedData(cacheKey, draft);
            }
            return draft;
        }
        catch (error) {
            console.error(`Failed to fetch draft ${draftId}:`, error);
            return null;
        }
    }
    clearCache() {
        try {
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith(this.cacheKey)) {
                    localStorage.removeItem(key);
                }
            });
        }
        catch {
            // Ignore cache errors in localStorage
        }
    }
}
exports.HashnodeContentProvider = HashnodeContentProvider;
function createContentProvider(_config) {
    // Content is sourced from the local Markdown backup (Hashnode GitHub backup),
    // not the deprecated Hashnode GraphQL API. The argument is kept for
    // backward compatibility with existing route loaders.
    return (0, local_content_1.createLocalContentProvider)();
}
